import {
  formatInteractionFieldValue,
  InteractionDisplayValue,
} from '../../../utils/interaction-note-format';

/** A display value that has something to show — everything except `omitted`. */
export type ShownValue = Exclude<InteractionDisplayValue, { kind: 'omitted' }>;

export interface ShownField {
  label: string;
  display: ShownValue;
}

export interface ParsedInteraction {
  title: string;
  fields: ShownField[];
}

/**
 * Pull the answered fields out of a log's `interactionForms`, dropping anything
 * structural or empty. Returns null when the log carries no interaction data.
 *
 * Shared by the desktop History & Notes panel and the mobile history timeline: both
 * receive the same log rows from the BFF, so both should read the same answers out of
 * them rather than each inventing a parse.
 */
export const parseInteractionLog = (data: unknown): ParsedInteraction | null => {
  if (!data || typeof data !== 'object') return null;
  const forms = (data as Record<string, unknown>).interactionForms as
    | Record<string, unknown>
    | undefined;
  if (!forms) return null;

  // `log.data` is untyped BFF payload, so neither level is guaranteed to be an array.
  // A malformed row must render as "no answers" rather than throw and take the whole
  // history panel down with it.
  const containers: unknown[] = Array.isArray(forms.containers) ? forms.containers : [];

  const fields = containers
    .flatMap((container) => {
      const own = (container as { fields?: unknown } | null)?.fields;
      return Array.isArray(own)
        ? (own as Array<{ title?: string; value?: unknown; type?: string }>)
        : [];
    })
    .map((field) => ({
      label: field?.title ?? '',
      // `?? {}` because a null entry would throw on destructuring in the formatter.
      display: formatInteractionFieldValue(field ?? {}),
    }))
    .filter((entry): entry is ShownField => entry.display.kind !== 'omitted');

  return { title: String(forms.title ?? ''), fields };
};
